"use client"

import { useEffect } from "react"
import posthog from "posthog-js"

// Catches chunk-load failures (user has app open through a deploy, then
// navigates and the old hashed chunk filename 404s). Auto-reload once per
// session — fresh load fetches the new chunk hashes and recovers. Guarded
// against reload loops so a genuinely broken build still surfaces the
// manual retry UI on the second crash.
const RELOAD_SENTINEL = "ha-chunk-reload-attempt"

function looksLikeChunkLoad(error: Error): boolean {
  if (error.name === "ChunkLoadError") return true
  const haystack = `${error.message ?? ""} ${error.stack ?? ""}`
  return haystack.includes("/_next/static/chunks/")
}

export default function ErrorBoundary({
  error,
  reset,
}: {
  error: Error & { digest?: string }
  reset: () => void
}) {
  useEffect(() => {
    posthog.capture("react_error_boundary_triggered", {
      boundary: "root",
      error_name: error.name || "Error",
      error_message: error.message?.slice(0, 300),
      error_stack: error.stack?.slice(0, 1000),
      digest: error.digest,
    })
    if (typeof window === "undefined") return
    if (!looksLikeChunkLoad(error)) return
    if (sessionStorage.getItem(RELOAD_SENTINEL)) return
    sessionStorage.setItem(RELOAD_SENTINEL, "1")
    window.location.reload()
  }, [error])

  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-3 p-6 text-center">
      <h1 className="text-lg font-semibold">Something went wrong</h1>
      <p className="text-sm text-muted-foreground max-w-md">
        Try refreshing the page. If the problem keeps happening, contact
        support and include the code below.
      </p>
      {error.digest && (
        <code className="rounded bg-muted px-2 py-1 text-xs">
          {error.digest}
        </code>
      )}
      <button
        type="button"
        onClick={reset}
        className="mt-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
      >
        Try again
      </button>
    </div>
  )
}
